import React, { useState, useEffect } from 'react';
import { mimeTypeDiagnostic, diagnoseMFIssues } from '../utils/mime-type-diagnostic';

interface QuickTestProps {
  onResultsUpdate: (hasErrors: boolean) => void;
  remoteUrl: string;
}

export function QuickTest({ onResultsUpdate, remoteUrl }: QuickTestProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<{
    remoteEntry: boolean;
    assets: boolean;
    cors: boolean;
    mimeTypes: boolean;
  } | null>(null);

  const runQuickTest = async () => {
    setIsRunning(true);
    const testResults = {
      remoteEntry: false,
      assets: false,
      cors: true,
      mimeTypes: false,
    };

    try {
      const remoteResult = await diagnoseMFIssues(remoteUrl);
      const remoteEntryDiagnostic = remoteResult.diagnostics.find((d) => d.url.includes('remoteEntry.js'));

      const isValidRemoteEntry =
        remoteEntryDiagnostic?.accessible &&
        remoteEntryDiagnostic?.mimeType &&
        !remoteEntryDiagnostic.mimeType.includes('text/html') &&
        (remoteEntryDiagnostic.mimeType.includes('application/javascript') ||
          remoteEntryDiagnostic.mimeType.includes('text/javascript'));

      testResults.remoteEntry = isValidRemoteEntry || false;
      testResults.mimeTypes = !remoteEntryDiagnostic?.mimeType?.includes('text/html');
      try {
        const commonAssets = await mimeTypeDiagnostic.diagnoseCommonAssets();
        testResults.assets = commonAssets.every((asset) => asset.accessible);
      } catch {
        testResults.assets = false;
      }

      setResults(testResults);
      const hasErrors = !Object.values(testResults).every(Boolean);
      onResultsUpdate(hasErrors);
    } catch (error) {
      onResultsUpdate(true);
    } finally {
      setIsRunning(false);
    }
  };

  useEffect(() => {
    runQuickTest();
  }, [remoteUrl]);

  const getStatusIcon = (status: boolean | undefined) => {
    if (status === undefined) return '⏳';
    return status ? '✅' : '❌';
  };

  const getStatusColor = (status: boolean | undefined) => {
    if (status === undefined) return 'text-neutral-500 dark:text-neutral-400';
    return status ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  };
  return (
    <div className="mb-6 p-4 border border-neutral-20 dark:border-neutral-70 rounded-lg bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-90/20 dark:to-indigo-900/20">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-lg font-semibold text-neutral-90 dark:text-white">⚡ Quick Health Check</h2>
        <button
          onClick={runQuickTest}
          disabled={isRunning}
          className="px-3 py-1 text-sm bg-primary-60 hover:bg-primary-70 disabled:bg-primary-40 text-white rounded transition-colors"
        >
          {isRunning ? '🔄 Testing...' : '🔄 Retest'}
        </button>
      </div>

      <div className="mb-3 text-xs text-neutral-600 dark:text-neutral-400">
        <span className="font-medium">Testing URL:</span> {remoteUrl.replace('https://', '').replace('http://', '')}
      </div>

      {results && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <span className={getStatusColor(results.remoteEntry)}>{getStatusIcon(results.remoteEntry)}</span>
            <span className="text-neutral-70 dark:text-neutral-30">Remote Entry</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={getStatusColor(results.cors)}>{getStatusIcon(results.cors)}</span>
            <span className="text-neutral-70 dark:text-neutral-30">CORS</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={getStatusColor(results.mimeTypes)}>{getStatusIcon(results.mimeTypes)}</span>
            <span className="text-neutral-70 dark:text-neutral-30">MIME Types</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={getStatusColor(results.assets)}>{getStatusIcon(results.assets)}</span>
            <span className="text-neutral-70 dark:text-neutral-30">Assets</span>
          </div>
        </div>
      )}

      {isRunning && (
        <div className="mt-3 flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
          <div className="animate-spin w-4 h-4 border-2 border-primary-60 border-t-transparent rounded-full"></div>
          Running diagnostics...
        </div>
      )}
    </div>
  );
}
